// views/alert/AlertAdminView.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLoading } from "@/store/loadingStore";
import { useModal } from "@/store/modalStore";
import { getAlertAdminsApi, deleteAlertAdminApi, AlertAdminResponse } from "@/lib/api/alertAdmin";
import "./AlertAdminView.css";

interface AdminData extends AlertAdminResponse {
  number: string;
}

const AlertAdminView = () => {
  const navigate = useNavigate();
  const { startLoading, stopLoading } = useLoading();
  const modal = useModal();

  // 상태 관리
  const [admins, setAdmins] = useState<AdminData[]>([]);
  const [selectedAdmins, setSelectedAdmins] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(0); // API는 0-based indexing
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);

  // 지역명을 문자열로 변환하는 헬퍼 함수
  const formatRegions = (regions: string[]): string => {
    if (!regions || regions.length === 0) return '-';
    return regions.join('/');
  };

  // 데이터 로드
  useEffect(() => {
    loadAdmins();
  }, [currentPage]);

  const loadAdmins = async () => {
    try {
      setLoading(true);
      startLoading('담당자 목록 조회 중...');
      
      const response = await getAlertAdminsApi({
        page: currentPage,
        size: 20,
        sort: 'createdAt',
        direction: 'desc'
      });
      
      // 번호 추가하여 AdminData로 변환
      const adminsWithNumber = response.content.map((admin, index) => ({
        ...admin,
        number: String(currentPage * 20 + index + 1).padStart(2, '0')
      }));
      
      setAdmins(adminsWithNumber);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
      
    } catch (error: any) {
      console.error("담당자 목록 조회 실패:", error);
      modal.alert("오류", error.message || "담당자 목록을 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
      stopLoading();
    }
  };

  // 신규등록
  const handleAddNew = () => {
    navigate("/alert/admin/register");
  };

  // 정보수정
  const handleEdit = (adminId: number) => {
    navigate(`/alert/admin/edit/${adminId}`);
  };

  // 담당자 삭제
  const handleDelete = (admin: AdminData) => {
    modal.confirm(
      "삭제 확인",
      `"${admin.name}" 담당자를 삭제하시겠습니까?`,
      async () => {
        try {
          startLoading('담당자 삭제 중...');
          await deleteAlertAdminApi(admin.id);
          modal.alert('성공', '담당자가 삭제되었습니다.');
          loadAdmins(); // 목록 새로고침
        } catch (error: any) {
          console.error('담당자 삭제 실패:', error);
          modal.alert('오류', error.message || '담당자 삭제에 실패했습니다.');
        } finally {
          stopLoading();
        }
      }
    );
  };

  // 페이지 변경
  const handlePageChange = (page: number) => {
    setCurrentPage(page - 1); // UI는 1-based, API는 0-based
  };

  return (
    <div className="alert-admin-container">
      {/* 상단 탭 */}
      <div className="tab-buttons">
        <button
          className="tab-button"
          onClick={() => {
            window.location.href = "/alert";
          }}
        >
          나주시 경고알림 현황
        </button>
        <button
          className="tab-button active"
          onClick={() => {
            window.location.href = "/alert/admin";
          }}
        >
          경고알림 관리자
        </button>
        <button
          className="tab-button"
          onClick={() => {
            window.open("https://gabia.com", "_blank");
          }}
        >
          문자충전
        </button>
      </div>

      {/* 메인 컨텐츠 */}
      <div className="admin-content">
        <div className="content-header">
          <h2 className="page-title">알림미 담당자 목록 ({totalElements}명)</h2>
          <button className="btn-add-new" onClick={handleAddNew}>
            신규등록
          </button>
        </div>

        {/* 테이블 */}
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>번호</th>
                <th>이름</th>
                <th>연락처</th>
                <th>부서</th>
                <th>관리지역</th>
                <th>정보수정</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center" style={{ padding: '40px' }}>
                    데이터를 불러오는 중...
                  </td>
                </tr>
              ) : admins.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center" style={{ padding: '40px' }}>
                    등록된 담당자가 없습니다.
                  </td>
                </tr>
              ) : (
                admins.map((admin) => (
                  <tr key={admin.id}>
                    <td className="text-center">{admin.number}</td>
                    <td className="text-center">{admin.name}</td>
                    <td className="text-center">{admin.contact}</td>
                    <td>{admin.department || '-'}</td>
                    <td>{formatRegions(admin.regions)}</td>
                    <td className="text-center">
                      <button className="btn-edit" onClick={() => handleEdit(admin.id)}>
                        정보수정
                      </button>
                      <button 
                        className="btn-delete" 
                        onClick={() => handleDelete(admin)}
                        style={{ marginLeft: '8px' }}
                      >
                        삭제
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              className="page-nav first"
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 0}
            >
              «
            </button>
            <button
              className="page-nav prev"
              onClick={() => handlePageChange(currentPage)}
              disabled={currentPage === 0}
            >
              ‹
            </button>

            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else {
                const startPage = Math.max(1, Math.min(currentPage + 1 - 2, totalPages - 4));
                pageNum = startPage + i;
              }
              
              return (
                <button
                  key={pageNum}
                  className={`page-number ${currentPage === pageNum - 1 ? "active" : ""}`}
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              className="page-nav next"
              onClick={() => handlePageChange(currentPage + 2)}
              disabled={currentPage >= totalPages - 1}
            >
              ›
            </button>
            <button
              className="page-nav last"
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage >= totalPages - 1}
            >
              »
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertAdminView;
